/**
 * Error Filtering Helper
 *
 * Shared utilities for filtering out external errors (browser extensions, third-party scripts, etc.)
 * from error reporting. Used by both admin-web and user-web.
 */

/**
 * Firebase auth error codes that are expected and should be ignored
 */
export const IGNORED_FIREBASE_AUTH_CODES = [
  'auth/cancelled-popup-request',
  'auth/popup-closed-by-user',
  'auth/provider-already-linked',
  'auth/network-request-failed',
  'auth/internal-error',
  'auth/popup-blocked'
]

/**
 * Common error messages that should be ignored (noise from browsers, extensions, etc.)
 */
export const COMMON_IGNORED_ERRORS = [
  'Connection to Indexed Database server lost. Refresh the page to try again',
  'Failed to get document because the client is offline',
  'UnknownError Connection to Indexed Database server lost. Refresh the page to try again',
  'error opening backing store for indexedDB',
  "Failed to read the 'localStorage' property from 'Window'",
  'Access is denied for this document',
  'Object Not Found Matching Id:',
  'FILE_ERROR_NO_SPACE', // Chrome IndexedDB write failure due to local disk being full — not actionable
  'UnknownError Internal error.', // Generic browser-side IndexedDB failure — not actionable
  'TrackerStorageType is not defined',
  'maps.googleapis.com',
  'maps-api-v3',
  'shell-plugins',
  'site-config',
  // Transient network failures (no stack, unactionable) — browser-specific messages
  'Load failed', // Safari
  'Failed to fetch', // Chrome / Edge
  'NetworkError when attempting to fetch resource' // Firefox
]

/**
 * Patterns that indicate an error originates from browser extensions or external scripts
 */
const EXTERNAL_PATTERNS = [
  // Browser extension URL schemes
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'webkit-masked-url://',
  'extension://',

  // Common extension/plugin identifiers
  'shell-plugins',
  'site-config',
  'content-script',
  'inject.js',
  'injected.js',

  // Ad blockers and privacy extensions
  'adblock',
  'ublock',
  'ghostery',
  'privacy-badger',

  // Password managers
  'lastpass',
  '1password',
  'bitwarden',
  'dashlane',

  // Other common extensions
  'grammarly',
  'honey',
  'metamask'
]

/**
 * Domains that are considered "our" domains (not external)
 */
const OUR_DOMAINS = ['coursely.se', 'localhost', '127.0.0.1']

/**
 * Checks if an error is an expected Firebase auth error that should be ignored
 */
export const isIgnoredFirebaseAuthError = (err: any): boolean => {
  return IGNORED_FIREBASE_AUTH_CODES.includes(err?.code)
}

/**
 * Checks if an error originates from browser extensions or external scripts
 */
export const isExternalScriptError = (err: any): boolean => {
  const errorMessage = err?.message || err?.toString() || ''
  const errorStack = err?.stack || ''

  // Check if error message or stack contains external patterns
  if (
    EXTERNAL_PATTERNS.some(
      (pattern) =>
        errorMessage.toLowerCase().includes(pattern.toLowerCase()) ||
        errorStack.toLowerCase().includes(pattern.toLowerCase())
    )
  ) {
    return true
  }

  // Check for masked URLs (Safari extensions often use this)
  if (errorStack.includes('masked-url') || errorStack.includes('hidden/')) {
    return true
  }

  // Check if stack trace points to external domains (not our app)
  const stackLines = errorStack.split('\n')
  const hasExternalDomain = stackLines.some((line: string) => {
    // Skip lines that don't contain URLs
    if (!line.includes('http')) return false

    // Check if URL is NOT from our domains
    const isOurDomain = OUR_DOMAINS.some((domain) => line.includes(domain))

    return !isOurDomain
  })

  if (hasExternalDomain) {
    return true
  }

  return false
}

/**
 * Checks if the current page is embedded in an iframe
 */
export const isInIframe = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.self !== window.top
  } catch {
    // If we can't access window.top due to CORS, we're definitely in an iframe
    return true
  }
}

/**
 * Checks if an error likely originates from the parent page's JavaScript (when in iframe)
 */
export const isParentPageError = (err: any): boolean => {
  // If not in iframe, error is definitely from our page
  if (!isInIframe()) {
    return false
  }

  // SecurityError is common when iframe tries to access parent page or vice versa
  if (err?.name === 'SecurityError') {
    return true
  }

  // Check if error message contains common cross-origin/iframe related errors
  const errorMessage = err?.message || err?.toString() || ''
  const crossOriginErrors = [
    'The operation is insecure',
    'cross-origin',
    'Permission denied to access property',
    'Blocked a frame with origin',
    'SecurityError'
  ]

  if (crossOriginErrors.some((text) => errorMessage.includes(text))) {
    return true
  }

  // Check stack trace for parent page errors
  const errorStack = err?.stack || ''
  const stackLines = errorStack.split('\n').filter((line: string) => line.trim())

  // If stack trace is suspiciously short (1-2 lines), it's likely from parent page
  if (stackLines.length < 3) {
    return true
  }

  // Check if stack trace contains our typical React/component patterns
  const reactPatterns = ['react', 'Component', 'useEffect', 'useState', '.tsx', '.jsx']
  const hasReactPatterns = reactPatterns.some((p) => errorStack.includes(p))

  // If stack is short AND doesn't have React patterns, likely parent page error
  if (stackLines.length < 5 && !hasReactPatterns) {
    return true
  }

  // Check for destructuring errors that might come from parent page
  const destructuringErrors = ['cannot be destructured', 'Cannot destructure property', 'is not iterable']
  if (destructuringErrors.some((p) => errorMessage.includes(p))) {
    if (stackLines.length < 4) {
      return true
    }
  }

  return false
}

/**
 * Checks if an error should be ignored based on common patterns
 */
export const shouldIgnoreError = (err: any, additionalIgnoredErrors: Array<string | RegExp> = []): boolean => {
  // Check if error is from parent page when embedded in iframe
  if (isParentPageError(err)) {
    return true
  }

  // Check if error is from browser extensions or external scripts
  if (isExternalScriptError(err)) {
    return true
  }

  // Ignore AbortError - usually from cancelled fetch requests
  if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
    return true
  }

  // Check ignored error messages (supports both strings and regex)
  const errorMessage = err?.message || err?.toString() || ''
  const allIgnoredErrors = [...COMMON_IGNORED_ERRORS, ...additionalIgnoredErrors]

  for (const pattern of allIgnoredErrors) {
    if (pattern instanceof RegExp) {
      if (pattern.test(errorMessage)) {
        return true
      }
    } else if (typeof pattern === 'string') {
      if (errorMessage.includes(pattern)) {
        return true
      }
    }
  }

  // Check error stack for Google Maps API errors
  const errorStack = err?.stack || ''
  if (errorStack.includes('maps.googleapis.com') || errorStack.includes('maps-api-v3')) {
    return true
  }

  return false
}

/**
 * Gets the parent site URL when embedded in an iframe
 * Returns null if not in iframe or if access is blocked by CORS
 */
export const getParentSiteUrl = (): string | null => {
  if (!isInIframe()) {
    return null
  }

  try {
    // Try to access parent URL (will fail if CORS restricts it)
    return window.parent.location.href
  } catch {
    // CORS restriction - we can't access parent URL
    // Try to get referrer as fallback
    if (typeof document !== 'undefined' && document.referrer) {
      return document.referrer
    }
    return 'unknown (CORS restricted)'
  }
}
